/**
 * "Turn on notifications" banner (TRUS-668), app-wide for staff and members.
 *
 * Shown at the top of the signed-in shell on every full page load and after
 * every sign-in while ALL of these hold:
 *  - this browser can do web push, in the top-level main window (never the
 *    desktop app, a pop-out, an iframe tab or an embed: isWebPushSupported);
 *  - the server has push set up (`/web-push/config` enabled);
 *  - this browser has no active registration;
 *  - notifications are not blocked (`denied`: the settings toggle explains how
 *    to allow them instead);
 *  - (staff) "Push notifications on all your devices" is not off (TRUS-697).
 *
 * "Turn on" raises the browser's permission prompt directly from the click
 * (Safari requires a user gesture); nothing ever prompts on page load.
 * "Not now" hides it until the next page load or sign-in (lib/webPush).
 * Once this browser is registered it never shows again here.
 *
 * When turning on fails (TRUS-668) - from the click, or a silent sync on load -
 * the banner stays and says why (blocked / this browser can't / setup issue,
 * which lib/webPush has reported to the backend). The click also shows a toast.
 * Nothing retries on its own: "Try again" is another click. A browser that
 * cannot receive push gets no retry button.
 */
import { useEffect, useState } from 'react';
import { BellRing } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useWebPushControl, type WebPushPortal } from '@/hooks/useWebPush';
import { toastWebPushFailure, webPushFailureText } from '@/components/notifications/webPushFailureText';
import {
  dismissWebPushBanner,
  dropLegacyBannerDismissal,
  getLastWebPushFailure,
  isWebPushBannerDismissed,
} from '@/lib/webPush';
import { usePushMasterSwitch } from '@/hooks/usePushMasterSwitch';

export function WebPushBanner({ portal }: { portal: WebPushPortal }) {
  const { t } = useAppTranslation();
  const { status, failure, busy, enable } = useWebPushControl(portal);
  const [dismissed, setDismissed] = useState(isWebPushBannerDismissed);
  // TRUS-697: with "Push notifications on all your devices" off, nothing is
  // pushed to this browser anyway - don't nag to turn it on.
  const { enabled: pushOnAllDevices } = usePushMasterSwitch(portal === 'staff');

  // The first version remembered a dismiss forever; drop that flag.
  useEffect(() => {
    dropLegacyBannerDismissal();
  }, []);

  const failed = status === 'error' && !!failure;
  if (pushOnAllDevices === false) return null;
  if (dismissed || !(status === 'default' || failed)) return null;
  const canRetry = !failed || failure?.kind !== 'unsupported';

  const dismiss = () => {
    dismissWebPushBanner();
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label={t('messaging.webPush.bannerLabel', 'Desktop notifications')}
      className="flex-shrink-0 border-b-2 border-v2accent bg-v2accent-soft px-4 py-3 text-v2accent-ink"
      data-testid="web-push-banner"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-v2accent text-white">
            <BellRing className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold">
              {t('messaging.webPush.promptTitle', 'Turn on notifications')}
            </p>
            <p className="text-sm">
              {t(
                'messaging.webPush.promptText',
                'Get messages and calls on this computer even when WeaveIn is in the background.',
              )}
            </p>
            {failed && (
              <p className="mt-1 text-sm font-medium" role="alert" data-testid="web-push-banner-error">
                {webPushFailureText(failure, t)}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md px-3 py-1.5 text-sm font-medium hover:bg-v2accent/10"
          >
            {t('messaging.webPush.notNow', 'Not now')}
          </button>
          {canRetry && (
            <button
              type="button"
              disabled={busy}
              // Synchronous on purpose: enable() raises the prompt before any await.
              onClick={() => {
                void enable().then((s) => {
                  if (s === 'error' || s === 'denied') toastWebPushFailure(getLastWebPushFailure(), t);
                  // Blocked at the prompt: the settings toggle explains how to allow it.
                  if (s === 'denied') dismiss();
                });
              }}
              className="rounded-md bg-v2accent px-4 py-1.5 text-sm font-semibold text-white shadow-v2-1 hover:bg-v2accent/90 disabled:opacity-60"
            >
              {failed ? t('messaging.webPush.tryAgain', 'Try again') : t('messaging.webPush.turnOn', 'Turn on')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default WebPushBanner;
